import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import useUserStore from '../stores/useUserStore';
import AirspaceStatusPill from '../components/AirspaceStatusPill';
import {
  REFERENCE_TYPE_LABELS,
  REFERENCE_STATUS_LABELS,
  REFERENCE_STATUS_PILL_CLASSES,
  ReferenceType,
  ReferenceStatus,
  AirspaceStatus,
  userInitials,
} from '../utils/airspace-helpers';
import { formatEventDate } from '../utils/date-utils';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import FadeIn from '../components/ui/FadeIn';
import Skeleton, { SkeletonGroup, SkeletonText } from '../components/ui/Skeleton';

interface AirspaceData {
  event: {
    id: string;
    name: string;
    event_date: string;
    location: string | null;
    show_height_ft: number | null;
    airspace_status: AirspaceStatus;
    airspace_needed: boolean | null;
    airspace_no_location_ack_at?: string | null;
    airspace_drive_folder_id: string | null;
    airspace_decided_at: string | null;
  };
  activeRefs: Array<{
    id: string;
    type: ReferenceType;
    reference_number: string | null;
    status: ReferenceStatus;
    notes: string | null;
    drive_file_id: string | null;
    drive_file_name: string | null;
    updated_at: string;
    updated_by_firstname: string | null;
    updated_by_lastname: string | null;
  }>;
  archivedRefs: Array<{ id: string; type: ReferenceType; reference_number: string | null; status: ReferenceStatus; archived_at: string }>;
}

interface Props {
  eventId: string;
  onClose: () => void;
}

const driveFolderUrl = (folderId: string) =>
  `https://drive.google.com/drive/folders/${folderId}`;

const EventAirspaceModal: React.FC<Props> = ({ eventId, onClose }) => {
  const api = useApi();
  const history = useHistory();
  const user = useUserStore(s => s.user);
  const canManage = !!(user?.can_pilot || user?.can_admin || user?.isadmin);

  const [data, setData] = useState<AirspaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/airspace/events/${eventId}`, { silent: true })
      .then((d: AirspaceData) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e.message || 'Failed to load');
        setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleManage = () => {
    onClose();
    history.push(`/pilots/airspace?event=${eventId}`);
  };

  const footer = data ? (
    <>
      <button
        type="button"
        onClick={onClose}
        className={buttonClass('quiet', 'md')}
      >
        Close
      </button>
      {canManage && (
        <button
          type="button"
          onClick={handleManage}
          className={buttonClass('filled', 'md')}
        >
          Manage in Pilot Dashboard →
        </button>
      )}
    </>
  ) : undefined;

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={data ? data.event.name : 'Airspace'}
      meta={data ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <AirspaceStatusPill status={data.event.airspace_status} size="full" />
          <span>
            {formatEventDate(data.event.event_date)}
            {data.event.location ? ` · ${data.event.location}` : ''}
            {data.event.show_height_ft ? ` · ${data.event.show_height_ft} ft` : ''}
          </span>
        </div>
      ) : undefined}
      footer={footer}
    >
      {loading && (
        <SkeletonGroup label="Loading airspace data" className="space-y-3 py-2">
          <Skeleton className="h-4 w-1/3" />
          <SkeletonText lines={3} />
        </SkeletonGroup>
      )}
      {error && <FadeIn><Alert tone="error">Error: {error}</Alert></FadeIn>}
      {data && (
        <FadeIn>
          {/* References */}
          <div>
            <div className="text-xs uppercase tracking-wider text-on-surface-variant mb-2">
              References ({data.activeRefs.length})
            </div>
            {data.activeRefs.length === 0 ? (
              <EmptyState variant="area" className="!px-0 !py-2 !items-start !text-left">
                {data.event.airspace_no_location_ack_at
                  ? 'No location acknowledged.'
                  : data.event.airspace_needed === false
                  ? 'No authorization needed (pilot decision).'
                  : data.event.airspace_needed === true
                    ? 'No reference numbers added yet.'
                    : 'Pilot has not yet decided whether authorization is needed.'}
              </EmptyState>
            ) : (
              <ul className="divide-y divide-gray-800">
                {data.activeRefs.map(r => (
                  <li key={r.id} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-on-surface">
                          {REFERENCE_TYPE_LABELS[r.type] || r.type}
                        </div>
                        <div className="text-xs text-on-surface-variant font-mono mt-0.5">
                          {r.reference_number || <span className="text-on-surface-disabled">— no number —</span>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${REFERENCE_STATUS_PILL_CLASSES[r.status]}`}>
                          {REFERENCE_STATUS_LABELS[r.status]}
                        </span>
                        <span className="text-[10px] text-on-surface-variant" title={r.updated_at}>
                          {userInitials(r.updated_by_firstname, r.updated_by_lastname)} ·{' '}
                          {formatEventDate(r.updated_at)}
                        </span>
                      </div>
                    </div>
                    {r.notes && (
                      <div className="mt-2 text-xs text-on-surface-variant whitespace-pre-wrap break-words">
                        {r.notes}
                      </div>
                    )}
                    {r.drive_file_name && r.drive_file_id && (
                      <a
                        href={`https://drive.google.com/file/d/${r.drive_file_id}/view`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-xs text-sky-400 hover:text-sky-300"
                      >
                        📎 {r.drive_file_name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Drive folder */}
          {data.event.airspace_drive_folder_id && (
            <div className="mt-4 pt-3 border-t border-gray-800 text-xs">
              <a
                href={driveFolderUrl(data.event.airspace_drive_folder_id)}
                target="_blank"
                rel="noreferrer"
                className="text-sky-400 hover:text-sky-300"
              >
                📁 Open Drive folder for this event ↗
              </a>
            </div>
          )}

          {/* Archived attempts */}
          {data.archivedRefs.length > 0 && (
            <details className="mt-3 pt-3 border-t border-gray-800 text-xs">
              <summary className="cursor-pointer text-on-surface-variant hover:text-on-surface">
                Previous attempt(s) — {data.archivedRefs.length} archived
              </summary>
              <ul className="mt-2 space-y-1">
                {data.archivedRefs.map(r => (
                  <li key={r.id} className="text-on-surface-variant">
                    <span className="font-mono">{r.reference_number || '—'}</span>
                    <span className="mx-1">·</span>
                    {REFERENCE_TYPE_LABELS[r.type] || r.type}
                    <span className="mx-1">·</span>
                    <span className="italic">{REFERENCE_STATUS_LABELS[r.status]}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </FadeIn>
      )}
    </Dialog>
  );
};

export default EventAirspaceModal;
